'use client';

import { memo, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Stack,
  Chip,
  Box,
  IconButton,
  Button,
  Avatar,
  Tooltip,
  LinearProgress,
  alpha,
  keyframes,
} from '@mui/material';
import {
  SwapHoriz,
  Business,
  LocationOn,
  Phone,
  ChevronRight,
  LocalShipping,
  Timeline,
  AccessTime,
  Scale,
  Person,
  ArrowForward,
  Circle,
} from '@mui/icons-material';
import { themeColors } from '../../theme/theme';
import { STATUS_STYLES } from '../../constants/statusStyles';
import { formatDateTime } from '../../utils/formatDateTime';

// Animations
const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.3);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(99, 102, 241, 0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Ultra-modern ParcelCard component for displaying parcel information
 * Used in both BookingHistory and ActiveParcels
 *
 * @param {Object} booking - Parcel/booking data (normalized to support both formats)
 * @param {Function} onClick - Click handler
 */
const ParcelCard = memo(({ booking, onClick }) => {
  const statusStyle = STATUS_STYLES[booking?.parcelStatus] || STATUS_STYLES.default;

  // Support both naming conventions
  const id = booking.id || booking.parcelId || booking.parcelId || booking.parcel?.id;
  const isMultiStage = booking.isMultiStage || booking.parcelIsMultiStage || false;
  const handoffCount = booking.handoffCount || booking.parcelHandoffCount || 0;
  const currentLegSeq = booking.currentLegSequence || booking.parcelCurrentLegId || 1;
  const totalLegs = booking.totalLegs || booking.parcelTotalLegs || 1;
  const isFirmTrip = booking.isFirmTrip || booking.parcelIsFirmTrip || false;
  const status = booking.status || booking.parcelStatus;
  const price = booking.price || booking.parcelPrice;
  const parcelType = booking.parcelType || booking.itemType;
  const getParcelTypeLabel = (pt) => {
    if (!pt) return '';
    if (Array.isArray(pt))
      return pt.map((p) => (typeof p === 'string' ? p : p.label || p.name || JSON.stringify(p)));
    if (typeof pt === 'object') return pt.label || pt.name || JSON.stringify(pt);
    return String(pt);
  };
  const createdAt = booking.createdAt || booking.dateTime || booking.parcelCreatedAt;

  // Handle different address formats
  const getAddressLabel = (address) => {
    if (!address) return '';
    if (typeof address === 'string') return address;
    if (typeof address === 'object') {
      if (address.fullAddress) return address.fullAddress;
      if (address.label) return address.label;
      const parts = [];
      if (address.street) parts.push(address.street);
      if (address.area) parts.push(address.area);
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      if (address.pincode) parts.push(address.pincode);
      if (parts.length) return parts.join(', ');
    }
    return '';
  };

  const getShortAddress = (address) => {
    const full = getAddressLabel(address);
    if (full.length > 40) {
      return full.substring(0, 40) + '...';
    }
    return full || 'Location';
  };

  const fromLabel =
    getAddressLabel(booking.from) ||
    getAddressLabel(booking.parcelPickupAddress) ||
    getAddressLabel(booking.pickup) ||
    'Pickup Location';
  const toLabel =
    getAddressLabel(booking.to) ||
    getAddressLabel(booking.parcelDeliveryAddress) ||
    getAddressLabel(booking.delivery) ||
    'Delivery Location';

  const riderPhone = booking.riderPhone || booking.rider?.phone || booking.parcelRider?.phone || '';
  const riderName =
    booking.riderName ||
    booking.parcelRiderName ||
    booking.rider?.name ||
    booking.parcelRider?.name ||
    '';
  const riderId = booking.parcelRiderId || booking.rider?.id || booking.parcelRider?.id || '';

  const eta =
    booking.eta ||
    booking.parcelEta ||
    booking.estimatedArrival ||
    booking.parcelEstimatedArrival ||
    null;
  const riderLocation =
    booking.riderLocation ||
    booking.parcelRiderLocation ||
    booking.rider?.location ||
    booking.parcelRider?.location ||
    null;
  const lastKnownLocation = booking.lastKnownLocation || booking.parcelLastKnownLocation || null;
  const pickupLat =
    booking.parcelPickupAddress?.lat ||
    booking.parcelPickupLat ||
    booking.pickupLat ||
    booking.pickup?.lat ||
    null;
  const pickupLng =
    booking.parcelPickupAddress?.lng ||
    booking.parcelPickupLng ||
    booking.pickupLng ||
    booking.pickup?.lng ||
    null;
  const weight = booking.weight || booking.parcelWeight || booking.parcelWeightKg || null;
  const progress = booking.progress || booking.parcelProgress || null;

  // compute ETA from rider location to pick-up coordinates for ASSIGNED/PENDING
  const etaComputed = useMemo(() => {
    if (eta) {
      try {
        return typeof eta === 'string' ? eta : formatDateTime(eta);
      } catch (e) {
        return null;
      }
    }
    const stateUpper = (status || '').toUpperCase();
    if (!['ASSIGNED', 'PENDING', 'ASSIGNING'].includes(stateUpper)) return null;
    if (!riderLocation || (!pickupLat && pickupLat !== 0) || (!pickupLng && pickupLng !== 0))
      return null;

    const toRad = (v) => (v * Math.PI) / 180;
    const haversineKm = (lat1, lon1, lat2, lon2) => {
      const R = 6371;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    };

    const riderLat =
      riderLocation.lat ?? riderLocation.latitude ?? riderLocation.latLng?.lat ?? null;
    const riderLng =
      riderLocation.lng ?? riderLocation.longitude ?? riderLocation.latLng?.lng ?? null;
    if (riderLat == null || riderLng == null) return null;

    const distanceKm = haversineKm(riderLat, riderLng, pickupLat, pickupLng);
    const avgSpeedKph = 30;
    const mins = Math.round((distanceKm / avgSpeedKph) * 60);
    if (isNaN(mins)) return null;
    if (mins <= 0) return 'Arriving';
    if (mins < 60) return `${mins} min`;
    const hours = Math.floor(mins / 60);
    const minsLeft = mins % 60;
    return `${hours}h ${minsLeft}m`;
  }, [eta, riderLocation, pickupLat, pickupLng, status]);

  // Get status colors with improved palette
  const getStatusColors = (st) => {
    const statusMap = {
      PENDING: {
        bg: alpha('#f59e0b', 0.1),
        color: '#d97706',
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      },
      ASSIGNED: {
        bg: alpha('#3b82f6', 0.1),
        color: '#2563eb',
        gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      },
      PICKED_UP: {
        bg: alpha('#8b5cf6', 0.1),
        color: '#7c3aed',
        gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      },
      IN_TRANSIT: {
        bg: alpha('#6366f1', 0.1),
        color: '#4f46e5',
        gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
      },
      OUT_FOR_DELIVERY: {
        bg: alpha('#06b6d4', 0.1),
        color: '#0891b2',
        gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      },
      DELIVERED: {
        bg: alpha('#10b981', 0.1),
        color: '#059669',
        gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      },
      CANCELLED: {
        bg: alpha('#ef4444', 0.1),
        color: '#dc2626',
        gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      },
    };
    return (
      statusMap[st?.toUpperCase()] || {
        bg: '#f1f5f9',
        color: '#64748b',
        gradient: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
      }
    );
  };

  const statusColors = getStatusColors(status);
  const isActiveStatus = ['ASSIGNED', 'PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_DELIVERY'].includes(
    status?.toUpperCase()
  );

  return (
    <Card
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: isActiveStatus ? alpha(statusColors.color, 0.2) : '#e2e8f0',
        transition: 'all 0.25s ease',
        boxShadow: isActiveStatus
          ? `0 8px 32px ${alpha(statusColors.color, 0.12)}`
          : '0 4px 16px rgba(0, 0, 0, 0.04)',
        animation: `${fadeInUp} 0.4s ease-out`,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 16px 48px ${alpha(statusColors.color, 0.18)}`,
          borderColor: alpha(statusColors.color, 0.3),
        },
      }}
    >
      <CardActionArea
        onClick={() => onClick(booking)}
        aria-label={`Open booking ${id}`}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {/* Status Header Bar */}
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              background: statusColors.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography
                sx={{
                  fontWeight: 800,
                  color: '#fff',
                  fontSize: '0.95rem',
                }}
              >
                #{id}
              </Typography>

              {/* Multi-leg badge */}
              {isMultiStage && totalLegs > 1 && (
                <Chip
                  icon={<SwapHoriz sx={{ fontSize: 14, color: 'inherit' }} />}
                  label={`Leg ${currentLegSeq}/${totalLegs}`}
                  size="small"
                  sx={{
                    bgcolor: alpha('#fff', 0.2),
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    height: 24,
                    '& .MuiChip-icon': { color: '#fff' },
                  }}
                />
              )}

              {/* Firm Trip badge */}
              {isFirmTrip && (
                <Chip
                  icon={<Business sx={{ fontSize: 12, color: 'inherit' }} />}
                  label="Firm"
                  size="small"
                  sx={{
                    bgcolor: alpha('#fff', 0.2),
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    height: 24,
                  }}
                />
              )}
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              {/* ETA chip */}
              {etaComputed && (
                <Chip
                  icon={<AccessTime sx={{ fontSize: 14, color: 'inherit' }} />}
                  label={etaComputed}
                  size="small"
                  sx={{
                    bgcolor: alpha('#fff', 0.2),
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    height: 26,
                    backdropFilter: 'blur(8px)',
                  }}
                />
              )}

              {/* Status */}
              <Chip
                label={status?.replace(/_/g, ' ')}
                size="small"
                sx={{
                  bgcolor: '#fff',
                  color: statusColors.color,
                  fontWeight: 800,
                  fontSize: '0.7rem',
                  height: 26,
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                }}
              />
            </Stack>
          </Box>

          {/* Main Content */}
          <Box sx={{ p: 2.5 }}>
            {/* Progress bar for active deliveries */}
            {progress !== null && progress !== undefined && (
              <Box sx={{ mb: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#64748b',
                      fontWeight: 600,
                    }}
                  >
                    Delivery Progress
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: 800,
                      color: statusColors.color,
                    }}
                  >
                    {progress}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Number(progress)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: '#f1f5f9',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      background: statusColors.gradient,
                    },
                  }}
                />
              </Box>
            )}

            {/* Route visualization */}
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: '#f8fafc',
                border: '1px solid #f1f5f9',
                mb: 2,
              }}
            >
              <Stack spacing={2}>
                {/* Pickup */}
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 2,
                      bgcolor: alpha('#10b981', 0.12),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Circle sx={{ fontSize: 10, color: '#10b981' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        color: '#64748b',
                        fontWeight: 600,
                        mb: 0.25,
                      }}
                    >
                      PICKUP
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.85rem',
                        color: '#334155',
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {fromLabel}
                    </Typography>
                  </Box>
                </Stack>

                {/* Connector line */}
                <Box
                  sx={{
                    ml: 1.85,
                    borderLeft: '2px dashed #e2e8f0',
                    height: 16,
                  }}
                />

                {/* Delivery */}
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 2,
                      bgcolor: alpha('#ef4444', 0.12),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <LocationOn sx={{ fontSize: 16, color: '#ef4444' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        color: '#64748b',
                        fontWeight: 600,
                        mb: 0.25,
                      }}
                    >
                      DELIVERY
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.85rem',
                        color: '#334155',
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {toLabel}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Box>

            {/* Rider info (if assigned) */}
            {(riderName || riderId) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2.5,
                  bgcolor: alpha('#6366f1', 0.06),
                  border: '1px solid',
                  borderColor: alpha('#6366f1', 0.1),
                  mb: 2,
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: '#6366f1',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                  }}
                >
                  {(riderName || riderId)?.toString?.()?.charAt?.(0)?.toUpperCase() || 'R'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      color: '#334155',
                    }}
                  >
                    {riderName || `Rider #${riderId}`}
                  </Typography>
                  {riderPhone && (
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                      {riderPhone}
                    </Typography>
                  )}
                </Box>
                {riderPhone && (
                  <IconButton
                    size="small"
                    component="a"
                    href={`tel:${riderPhone}`}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      bgcolor: '#6366f1',
                      color: '#fff',
                      '&:hover': {
                        bgcolor: '#4f46e5',
                      },
                    }}
                  >
                    <Phone sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </Box>
            )}

            {/* Footer with price and meta */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 1 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                {/* Parcel type */}
                {parcelType && (
                  <Chip
                    label={
                      Array.isArray(parcelType)
                        ? getParcelTypeLabel(parcelType)[0]
                        : getParcelTypeLabel(parcelType)
                    }
                    size="small"
                    sx={{
                      bgcolor: '#f1f5f9',
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 24,
                    }}
                  />
                )}

                {/* Weight */}
                {weight && (
                  <Chip
                    icon={<Scale sx={{ fontSize: 12 }} />}
                    label={`${weight} kg`}
                    size="small"
                    sx={{
                      bgcolor: '#f1f5f9',
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 24,
                    }}
                  />
                )}
              </Stack>

              <Stack direction="row" spacing={1.5} alignItems="center">
                {/* Price */}
                {price && (
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '1.1rem',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    ₹{price}
                  </Typography>
                )}

                {/* View button */}
                <Button
                  size="small"
                  endIcon={<ChevronRight />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick(booking);
                  }}
                  sx={{
                    color: '#6366f1',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    textTransform: 'none',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    bgcolor: alpha('#6366f1', 0.08),
                    '&:hover': {
                      bgcolor: alpha('#6366f1', 0.15),
                    },
                  }}
                >
                  Details
                </Button>
              </Stack>
            </Stack>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
});

ParcelCard.displayName = 'ParcelCard';

export default ParcelCard;
